import { useEffect, useState } from 'react';
import VideoCameraOutlined from '@ant-design/icons/lib/icons/VideoCameraOutlined';
import { useTranslation } from 'react-i18next';

import { OpEmpty } from 'new-components/DLS/OpEmpty/OpEmpty';
import { OpList } from 'new-components/DLS/OpList/OpList';
import { OpSpin } from 'new-components/DLS/OpSpin/OpSpin';
import { OpTypography } from 'new-components/DLS/OpTypography/OpTypography';
import { useSelectorJs } from 'utils/customHooks';
import { getOpParams } from 'utils/getOpParams';

import { DATE_TIME_AM_PM_FORMAT } from '../../../../../constants/dates';
import { AltaVideoState } from '../../../../../store/slices/altaVideoSlice';
import { VisitsState } from '../../../../../store/slices/visitsSlice';
import { ImmutableRootState } from '../../../../../store/store';
import { toLocalByTimezone } from '../../../../../utils/dates';

import ActivityLogVideoModal from './ActivityLogVideoModal';

interface ActivityLogItem {
  id: string;
  time: string;
  type: string;
  video: boolean;
}

const ActivityLogTab = () => {
  const { t } = useTranslation();

  const { orgId } = getOpParams();
  const selectedVisit = useSelectorJs((state: ImmutableRootState) =>
    state.getIn(['visits', 'selectedVisit']),
  ) as VisitsState['selectedVisit'];
  const selectedVisitor = useSelectorJs((state: ImmutableRootState) =>
    state.getIn(['visits', 'selectedVisitor']),
  ) as VisitsState['selectedVisitor'];
  const videoDevices = useSelectorJs((state: ImmutableRootState) =>
    state.getIn(['altaVideo', 'videoDevices']),
  ) as AltaVideoState['videoDevices'];
  const devicesByWorkflow = useSelectorJs((state: ImmutableRootState) =>
    state.getIn(['altaVideo', 'devicesByWorkflow']),
  ) as AltaVideoState['devicesByWorkflow'];

  const [logs, setLogs] = useState<ActivityLogItem[] | null>(null);
  const [selectedLogType, setSeletedLogType] = useState<
    'SIGNIN' | 'SIGNOUT' | null
  >(null);
  const tz = selectedVisit?.location?.timezone;
  const selectedVisitSiteId = selectedVisit?.location?.id ?? null;
  const devicesByWorkflowForSite = selectedVisitSiteId
    ? devicesByWorkflow[selectedVisitSiteId]
    : null;

  useEffect(() => {
    if (!selectedVisitor) {
      setLogs([]);
      return;
    }

    const tempLogs: ActivityLogItem[] = [];
    // Denied Entry
    if (selectedVisitor.signIn && selectedVisitor.status === 99) {
      if (
        (devicesByWorkflowForSite?.signInDeviceId ?? []).some((id) =>
          videoDevices.some((v) => v.guId === id),
        )
      ) {
        tempLogs.push({
          id: 'denied',
          time: selectedVisitor.signIn,
          type: 'Denied',
          video: true,
        });
      }
      // Signed In
    } else if (selectedVisitor.signIn) {
      if (
        (devicesByWorkflowForSite?.signInDeviceId ?? []).some((id) =>
          videoDevices.some((v) => v.guId === id),
        )
      ) {
        tempLogs.push({
          id: 'signIn',
          time: selectedVisitor.signIn,
          type: 'Sign In',
          video: true,
        });
      }
    }
    // Signed Out
    if (
      selectedVisitor.signOut &&
      (devicesByWorkflowForSite?.signOutDeviceId ?? []).some((id) =>
        videoDevices.some((v) => v.guId === id),
      )
    ) {
      tempLogs.push({
        id: 'signOut',
        time: selectedVisitor.signOut,
        type: 'Sign Out',
        video: true,
      });
    }

    setLogs(tempLogs);
  }, [
    orgId,
    selectedVisitor,
    videoDevices,
    devicesByWorkflowForSite?.signInDeviceId,
    devicesByWorkflowForSite?.signOutDeviceId,
  ]);

  if (logs === null) return <OpSpin />;

  return (
    <>
      <OpList
        header={
          <OpTypography.Text>
            {t('visitors.profile.videoLog.title')}
          </OpTypography.Text>
        }
        bordered
        dataSource={logs}
        locale={{
          emptyText: <OpEmpty description={t('common.noData')} />,
        }}
        renderItem={(item) => {
          const log = item as ActivityLogItem;
          return (
            <OpList.Item key={log.id}>
              <div
                style={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'space-between',
                }}
              >
                <span>
                  {toLocalByTimezone(log.time, tz)?.format(
                    DATE_TIME_AM_PM_FORMAT,
                  )}
                </span>
                <span
                  style={{
                    color:
                      log.type === 'Sign Out'
                        ? '#999'
                        : log.type === 'Denied'
                          ? 'red'
                          : 'green',
                  }}
                >
                  {log.type}
                </span>
                {log.video && (
                  <VideoCameraOutlined
                    onClick={() =>
                      setSeletedLogType(
                        log.id === 'signOut' ? 'SIGNOUT' : 'SIGNIN',
                      )
                    } // sign in and denied both has sign in time
                    style={{ cursor: 'pointer', marginLeft: 8 }}
                  />
                )}
              </div>
            </OpList.Item>
          );
        }}
      />

      {selectedLogType && devicesByWorkflowForSite && (
        <ActivityLogVideoModal
          open={!!selectedLogType}
          onClose={() => setSeletedLogType(null)}
          targetType={selectedLogType}
          videoDevices={videoDevices}
          devicesByWorkflow={devicesByWorkflowForSite}
          selectedDeviceGuIds={
            selectedLogType === 'SIGNIN'
              ? devicesByWorkflowForSite.signInDeviceId
              : devicesByWorkflowForSite.signOutDeviceId
          }
        />
      )}
    </>
  );
};

export default ActivityLogTab;
